import { createSelector } from 'reselect';
import { IApplicationState } from '@bridebook/web/lib/types';
import { TEnquiryDates } from './types';
import {
  deriveInitialValues,
  missingEnquiryDetails,
} from '@bridebook/web/lib/enquiries/schemas/enquiry-form-schema';

const _getEnquiryDates = (state: IApplicationState) => state.enquiries.enquiryDates;
const _getEnquiries = (state: IApplicationState) => state.enquiries;

export const getEnquiryDates = createSelector([_getEnquiryDates], (enquiryDates) =>
  enquiryDates.reduce((acc, val) => {
    if (val?.supplierId && val?.enquired) {
      acc[val.supplierId] = val.enquired;
    }
    return acc;
  }, {} as TEnquiryDates),
);

export const getShowEnquiryForm = createSelector(
  _getEnquiries,
  ({ showEnquiryForm }) => showEnquiryForm,
);

export const getEnquiryFormStartInEditMode = createSelector(
  _getEnquiries,
  ({ startInEditMode }) => startInEditMode,
);

export const getShowMultipleEnquiryForm = createSelector(
  _getEnquiries,
  ({ multipleEnquiry }) => multipleEnquiry,
);

export const getEnquiryConfirmationSupplier = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.enquiryConfirmationSupplier,
);

export const getEnquiryConfirmationSupplierType = createSelector(
  getEnquiryConfirmationSupplier,
  (supplier) => supplier?.type[0],
);

export const getEnquiryConfirmationSupplierId = createSelector(
  getEnquiryConfirmationSupplier,
  (supplier) => supplier?.id,
);

export const getIsEnquiryConfirmationSupplierVenueType = createSelector(
  getEnquiryConfirmationSupplierType,
  (type) => type === 'venue',
);

export const getEnquiryConfirmationSupplierName = createSelector(
  getEnquiryConfirmationSupplier,
  (supplier) => supplier?.name || '',
);

export const getEnquiryCount = createSelector(
  getEnquiryDates,
  (enquiries) => enquiries.enquiryCount,
);

export const getEnquirySupplier = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.enquirySupplier,
);

export const getEnquiryConfirmationScreenShown = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.enquiryConfirmationScreenShown,
);

export const getEnquiryMultipleEnquiry = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.multipleEnquiry,
);

export const getEnquiryFormIntents = createSelector(_getEnquiries, (enquiries) => enquiries.intent);

export const getOnPageEnquiry = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.onPageEnquiry,
);

export const getEnquiryInfoProps = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.infoProps,
);

export const getEnquiryPlannerOrigin = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.plannerOrigin,
);

export const getEnquiryPlannerResults = createSelector(
  _getEnquiries,
  (enquiries) => enquiries.plannerResults,
);

export const getEnquiryContext = createSelector(
  getEnquiryInfoProps,
  (infoProps) => infoProps.context,
);

export const getEnquiryMethod = createSelector(
  getEnquiryInfoProps,
  (infoProps) => infoProps.method,
);

export const getEnquiryContextName = createSelector(
  getEnquiryContext,
  getEnquiryMethod,
  (context, method) => context ?? method,
);

// The details the couple's record still lacks for an enquiry, as the form would see them.
export const getMissingEnquiryDetails = createSelector(
  [
    (state: IApplicationState) => state.weddings.profile,
    (state: IApplicationState) => state.users.user,
  ],
  (profile, user) => missingEnquiryDetails(deriveInitialValues(profile, user)),
);
